import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getAdminPanelApiUrl, getAdminPanelPublicApiUrl } from '../../../config/api';
import './AdminChatManager.css';

const AdminChatManager = () => {
    const { t } = useTranslation();
    const [sessions, setSessions] = useState([]);
    const [selectedSession, setSelectedSession] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [filter, setFilter] = useState('active');
    const [hasMore, setHasMore] = useState(false);
    const [offset, setOffset] = useState(0);
    const [isMobilePopupOpen, setIsMobilePopupOpen] = useState(false);

    const fetchSessions = async () => {
        setIsLoading(true);
        try {
            console.log('Fetching sessions with filter:', filter);
            const response = await fetch(`${getAdminPanelApiUrl('chat/admin_get_chat_sessions.php')}?status=${filter}&limit=20&offset=${offset}`, {
                credentials: 'include'
            });
            const data = await response.json();
            console.log('Sessions API response:', data);

            if (data.status === 'success') {
                console.log('Setting sessions:', data.sessions);
                setSessions(data.sessions);
                setHasMore(data.has_more);
                console.log('Sessions updated, count:', data.sessions.length);
            } else {
                console.error('API returned error:', data);
            }
        } catch (error) {
            console.error('Error fetching sessions:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchMessages = async (sessionId) => {
        try {
            console.log('Fetching messages for session:', sessionId);
            const response = await fetch(`${getAdminPanelPublicApiUrl('chat_get_messages.php')}?session_id=${sessionId}&admin=true`, {
                credentials: 'include'
            });
            const data = await response.json();
            console.log('Messages API response:', data);

            if (data.status === 'success') {
                console.log('Setting messages:', data.messages);
                setMessages(data.messages);
                console.log('Messages state updated, count:', data.messages.length);
            } else {
                console.error('API returned error:', data);
            }
        } catch (error) {
            console.error('Error fetching messages:', error);
        }
    };

    const sendMessage = async () => {
        if (!newMessage.trim() || !selectedSession) return;

        const messageToSend = newMessage.trim();
        setNewMessage('');

        try {
            const response = await fetch(getAdminPanelPublicApiUrl('chat_send_message.php'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    session_id: selectedSession.session_id,
                    message: messageToSend,
                    sender_type: 'admin'
                })
            });

            const data = await response.json();

            if (data.status === 'success') {
                setMessages(prev => [...prev, {
                    id: data.message_id,
                    sender_type: 'admin',
                    message: messageToSend,
                    created_at: data.timestamp
                }]);
            } else {
                alert(data.message);
                setNewMessage(messageToSend);
            }
        } catch (error) {
            console.error('Error sending message:', error);
            alert('Failed to send message');
            setNewMessage(messageToSend);
        }
    };

    const closeSession = async (sessionId) => {
        if (!window.confirm('Are you sure you want to close this chat session?')) return;

        try {
            const response = await fetch(getAdminPanelApiUrl('chat/admin_close_chat_session.php'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({ session_id: sessionId })
            });

            const data = await response.json();

            if (data.status === 'success') {
                fetchSessions();
                if (selectedSession && selectedSession.session_id === sessionId) {
                    setSelectedSession(null);
                    setMessages([]);
                }
            } else {
                alert(data.message);
            }
        } catch (error) {
            console.error('Error closing session:', error);
            alert('Failed to close session');
        }
    };

    const selectSession = (session) => {
        setSelectedSession(session);
        fetchMessages(session.session_id);

        // На мобильных устройствах открываем pop-up
        if (window.innerWidth <= 768) {
            setIsMobilePopupOpen(true);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString();
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'active': return '#10b981';
            case 'closed': return '#ef4444';
            case 'pending': return '#f59e0b';
            default: return '#6b7280';
        }
    };

    const closeMobilePopup = () => {
        setIsMobilePopupOpen(false);
        setSelectedSession(null);
        setMessages([]);
    };

    useEffect(() => {
        fetchSessions();
    }, [filter]);

    // Автоматическое обновление каждые 10 секунд
    useEffect(() => {
        const interval = setInterval(() => {
            console.log('Auto-refreshing sessions...');
            fetchSessions();
        }, 10000); // 10 секунд

        return () => clearInterval(interval);
    }, [filter, fetchSessions]);

    // Автоматическое обновление сообщений каждые 5 секунд, когда выбрана сессия
    useEffect(() => {
        if (!selectedSession) return;

        const interval = setInterval(() => {
            fetchMessages(selectedSession.session_id);
        }, 5000); // 5 секунд

        return () => clearInterval(interval);
    }, [selectedSession]);

    return (
        <div className="admin-chat-manager">
            <div className="chat-header">
                <h2>Chat Management</h2>
                <div className="filter-controls">
                    <select
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        className="filter-select"
                    >
                        <option value="all">All Sessions</option>
                        <option value="active">Active</option>
                        <option value="closed">Closed</option>
                        <option value="pending">Pending</option>
                    </select>
                </div>
            </div>

            <div className="chat-layout">
                <div className="sessions-list">
                    <h3>Chat Sessions</h3>
                    {isLoading ? (
                        <div className="loading">Loading sessions...</div>
                    ) : (
                        <div className="sessions-container">
                            {sessions.map((session) => (
                                <div
                                    key={session.id}
                                    className={`session-item ${selectedSession?.id === session.id ? 'selected' : ''}`}
                                    onClick={() => selectSession(session)}
                                >
                                    <div className="session-header">
                                        <div className="client-info">
                                            <strong>{session.client_name}</strong>
                                            <span className="client-email">{session.client_email}</span>
                                        </div>
                                        <div className="session-status">
                                            <span
                                                className="status-badge"
                                                style={{ backgroundColor: getStatusColor(session.status) }}
                                            >
                                                {session.status}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="session-details">
                                        <div className="last-message">
                                            {session.last_message ?
                                                (session.last_message.length > 50 ?
                                                    session.last_message.substring(0, 50) + '...' :
                                                    session.last_message
                                                ) : 'No messages'
                                            }
                                        </div>
                                        <div className="session-meta">
                                            <span className="unread-count">
                                                {session.unread_count > 0 && (
                                                    <span className="unread-badge">{session.unread_count}</span>
                                                )}
                                            </span>
                                            <span className="last-activity">
                                                {formatDate(session.last_activity)}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="session-actions">
                                        <button
                                            className="close-session-btn"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                closeSession(session.session_id);
                                            }}
                                            title="Close session"
                                        >
                                            ×
                                        </button>
                                    </div>
                                </div>
                            ))}

                            {sessions.length === 0 && (
                                <div className="no-sessions">No chat sessions found</div>
                            )}
                        </div>
                    )}
                </div>

                <div className="chat-conversation">
                    {selectedSession ? (
                        <>
                            <div className="conversation-header">
                                <div className="conversation-info">
                                    <h4>{selectedSession.client_name}</h4>
                                    <span className="conversation-email">{selectedSession.client_email}</span>
                                    {selectedSession.client_phone && (
                                        <span className="conversation-phone">{selectedSession.client_phone}</span>
                                    )}
                                </div>
                                <div className="conversation-status">
                                    <span
                                        className="status-badge"
                                        style={{ backgroundColor: getStatusColor(selectedSession.status) }}
                                    >
                                        {selectedSession.status}
                                    </span>
                                </div>
                            </div>

                            <div className="messages-container">
                                {console.log('Rendering messages:', messages)}
                                {console.log('Messages count:', messages.length)}
                                {messages.length === 0 && (
                                    <div className="no-messages">No messages yet</div>
                                )}
                                {messages.map((message, index) => {
                                    console.log(`Rendering message ${index}:`, message);
                                    return (
                                        <div
                                            key={message.id}
                                            className={`message ${message.sender_type === 'admin' ? 'admin' : 'client'}`}
                                        >
                                            <div className="message-content">
                                                {message.message}
                                            </div>
                                            <div className="message-time">
                                                {formatDate(message.created_at)}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="message-input-container">
                                <textarea
                                    value={newMessage}
                                    onChange={(e) => setNewMessage(e.target.value)}
                                    placeholder="Type your message..."
                                    rows="2"
                                />
                                <button
                                    className="send-btn"
                                    onClick={sendMessage}
                                    disabled={!newMessage.trim()}
                                >
                                    Send
                                </button>
                            </div>
                        </>
                    ) : (
                        <div className="no-conversation">
                            <p>Select a chat session to view messages</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Mobile Pop-up */}
            {isMobilePopupOpen && selectedSession && (
                <div className="mobile-chat-popup">
                    <div className="mobile-chat-popup-content">
                        <div className="mobile-chat-header">
                            <div className="mobile-chat-info">
                                <h4>{selectedSession.client_name}</h4>
                                <span className="mobile-chat-email">{selectedSession.client_email}</span>
                                {selectedSession.client_phone && (
                                    <span className="mobile-chat-phone">{selectedSession.client_phone}</span>
                                )}
                            </div>
                            <div className="mobile-chat-actions">
                                <span
                                    className="mobile-status-badge"
                                    style={{ backgroundColor: getStatusColor(selectedSession.status) }}
                                >
                                    {selectedSession.status}
                                </span>
                                <button
                                    className="mobile-close-btn"
                                    onClick={closeMobilePopup}
                                    title="Close"
                                >
                                    ×
                                </button>
                            </div>
                        </div>

                        <div className="mobile-messages-container">
                            {messages.length === 0 && (
                                <div className="no-messages">No messages yet</div>
                            )}
                            {messages.map((message, index) => (
                                <div
                                    key={message.id}
                                    className={`message ${message.sender_type === 'admin' ? 'admin' : 'client'}`}
                                >
                                    <div className="message-content">
                                        {message.message}
                                    </div>
                                    <div className="message-time">
                                        {formatDate(message.created_at)}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="mobile-message-input-container">
                            <textarea
                                value={newMessage}
                                onChange={(e) => setNewMessage(e.target.value)}
                                placeholder="Type your message..."
                                rows="2"
                            />
                            <button
                                className="mobile-send-btn"
                                onClick={sendMessage}
                                disabled={!newMessage.trim()}
                            >
                                Send
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminChatManager;